import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getClassSchedule, saveClassSchedule, removeClassSchedule } from '../../Api';
import LoadingSpinner from '../../components/Loading';

// 1 = segunda … 6 = sábado (igual Date.getDay())
const DIAS = [
    { id: 1, curto: 'Seg', nome: 'Segunda' },
    { id: 2, curto: 'Ter', nome: 'Terça' },
    { id: 3, curto: 'Qua', nome: 'Quarta' },
    { id: 4, curto: 'Qui', nome: 'Quinta' },
    { id: 5, curto: 'Sex', nome: 'Sexta' },
    { id: 6, curto: 'Sáb', nome: 'Sábado' },
];

const TIPOS = {
    REGENTE: { rotulo: 'Regente', bg: '#dbeafe', cor: '#1d4ed8', borda: '#2563eb' },
    ED_FISICA: { rotulo: 'Ed. Física', bg: '#dcfce7', cor: '#15803d', borda: '#16a34a' },
};

const LINHAS_PADRAO = 5;
const linhaVazia = () => ({ inicio: '', fim: '' });

const card = {
    width: '100%', maxWidth: 980, boxSizing: 'border-box', margin: '0 auto',
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
    border: '1px solid var(--line, #e3e8eb)', borderRadius: 14, padding: '22px 22px 18px',
};
const btn = (primario) => ({
    padding: '11px 20px', borderRadius: 10, fontWeight: 800, cursor: 'pointer',
    border: primario ? 'none' : '1px solid var(--line, #d0d7db)',
    background: primario ? 'var(--teal, #003e4f)' : 'transparent',
    color: primario ? '#fff' : 'var(--text, #16323b)',
});
const aviso = (tom) => ({
    borderRadius: 10, padding: '10px 12px', fontSize: 14, margin: '0 0 12px',
    ...(tom === 'laranja' && { background: '#fff4e5', border: '1px solid #f59e0b', color: '#7c4a03' }),
    ...(tom === 'vermelho' && { background: '#fde8e8', border: '1px solid #dc2626', color: '#991b1b' }),
    ...(tom === 'verde' && { background: '#e3f4ec', border: '1px solid #16a34a', color: '#166534' }),
    ...(tom === 'azul' && { background: '#e8f0fe', border: '1px solid #2563eb', color: '#1e3a8a' }),
});
const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15, 30, 40, .45)', backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
};
const modal = {
    background: 'var(--surface, #fff)', color: 'var(--text, #16323b)', borderRadius: 14,
    maxWidth: 520, width: '100%', padding: '22px 22px 18px', boxShadow: '0 20px 50px rgba(0,0,0,.25)',
};

const CSS_IMPRESSAO = `
@media print {
  body * { visibility: hidden; }
  #grade-horaria-print, #grade-horaria-print * { visibility: visible; }
  #grade-horaria-print { position: absolute; left: 0; top: 0; width: 100%; }
  .nao-imprimir { display: none !important; }
  @page { size: A4 landscape; margin: 12mm; }
  #grade-horaria-print td, #grade-horaria-print th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

const ClassSchedule = () => {
    const navigate = useNavigate();
    const { id_class } = useParams();
    const location = useLocation();
    // aberto pela área do professor (?ver=1) → sempre só leitura
    const somenteLeitura = new URLSearchParams(location.search).get('ver') === '1';
    // cargo de quem está logado: só direção/secretaria editam
    const cargoGestor = ['DIRETOR/SUPERVISOR', 'SECRETARIO'].includes(localStorage.getItem('position_at_school'));

    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [turma, setTurma] = useState(null);
    const [podeEditar, setPodeEditar] = useState(false);
    const [existe, setExiste] = useState(false);
    const [sabado, setSabado] = useState(false);
    const [horarios, setHorarios] = useState(Array.from({ length: LINHAS_PADRAO }, linhaVazia));
    const [celulas, setCelulas] = useState({}); // "dia-slot" -> 'REGENTE' | 'ED_FISICA'
    const [msg, setMsg] = useState(null);
    const [confirmar, setConfirmar] = useState(null); // 'salvar' | 'remover'

    useEffect(() => {
        (async () => {
            try {
                const r = await getClassSchedule(id_class);
                const d = r?.data || {};
                setTurma(d.turma || null);
                setPodeEditar(!!d.podeEditar);
                if (d.grade) {
                    setExiste(true);
                    setSabado(!!d.grade.sabado);
                    setHorarios((d.grade.horarios || []).length ? d.grade.horarios.map(h => ({ inicio: h.inicio || '', fim: h.fim || '' })) : [linhaVazia()]);
                    const m = {};
                    (d.grade.celulas || []).forEach(c => { m[`${c.dia}-${c.slot}`] = c.tipo; });
                    setCelulas(m);
                }
            } catch (e) {
                setMsg({ tom: 'vermelho', texto: e?.response?.data?.msg || 'Não foi possível carregar o horário da turma.' });
            } finally {
                setLoading(false);
            }
        })();
    }, [id_class]);

    const dias = useMemo(() => DIAS.filter(d => sabado || d.id !== 6), [sabado]);

    // resumo: aulas por semana e dias de cada tipo (só o que está visível na grade)
    const resumo = useMemo(() => {
        const r = { REGENTE: { aulas: 0, dias: new Set() }, ED_FISICA: { aulas: 0, dias: new Set() } };
        Object.entries(celulas).forEach(([k, tipo]) => {
            const [dia, slot] = k.split('-').map(Number);
            if (!r[tipo] || slot >= horarios.length || (dia === 6 && !sabado)) return;
            r[tipo].aulas += 1;
            r[tipo].dias.add(dia);
        });
        const nomes = (set) => DIAS.filter(d => set.has(d.id)).map(d => d.curto).join(', ');
        return {
            REGENTE: { aulas: r.REGENTE.aulas, dias: nomes(r.REGENTE.dias) },
            ED_FISICA: { aulas: r.ED_FISICA.aulas, dias: nomes(r.ED_FISICA.dias) },
        };
    }, [celulas, horarios.length, sabado]);

    const semAulas = resumo.REGENTE.aulas + resumo.ED_FISICA.aulas === 0;
    const faltaRegente = !!(turma?.regente || turma?.adjunto) && resumo.REGENTE.aulas === 0;
    const faltaEdFisica = !!turma?.edFisica && resumo.ED_FISICA.aulas === 0;

    const setCelula = (dia, slot, tipo) => {
        setCelulas(prev => {
            const n = { ...prev };
            if (tipo) n[`${dia}-${slot}`] = tipo; else delete n[`${dia}-${slot}`];
            return n;
        });
    };

    const setHora = (i, campo, valor) => {
        setHorarios(prev => prev.map((h, j) => (j === i ? { ...h, [campo]: valor } : h)));
    };

    const addLinha = () => setHorarios(prev => (prev.length >= 15 ? prev : [...prev, linhaVazia()]));

    const removerLinha = (i) => {
        if (horarios.length <= 1) return;
        setHorarios(prev => prev.filter((_, j) => j !== i));
        // reindexa as células das linhas abaixo
        setCelulas(prev => {
            const n = {};
            Object.entries(prev).forEach(([k, t]) => {
                const [dia, slot] = k.split('-').map(Number);
                if (slot === i) return;
                n[`${dia}-${slot > i ? slot - 1 : slot}`] = t;
            });
            return n;
        });
    };

    const preencherRegente = () => {
        setCelulas(prev => {
            const n = { ...prev };
            dias.forEach(d => horarios.forEach((_, s) => { if (!n[`${d.id}-${s}`]) n[`${d.id}-${s}`] = 'REGENTE'; }));
            return n;
        });
    };

    const payload = () => ({
        id_class,
        sabado,
        horarios,
        celulas: Object.entries(celulas)
            .map(([k, tipo]) => { const [dia, slot] = k.split('-').map(Number); return { dia, slot, tipo }; })
            .filter(c => c.slot < horarios.length && (sabado || c.dia !== 6)),
    });

    const pedirSalvar = () => {
        setMsg(null);
        if (semAulas) {
            setMsg({ tom: 'vermelho', texto: 'Marque pelo menos uma aula na grade antes de salvar.' });
            return;
        }
        setConfirmar('salvar');
    };

    const salvar = async () => {
        setConfirmar(null);
        setSalvando(true);
        try {
            await saveClassSchedule(payload());
            setExiste(true);
            setMsg({ tom: 'verde', texto: 'Horário da turma salvo com sucesso.' });
        } catch (e) {
            setMsg({ tom: 'vermelho', texto: e?.response?.data?.msg || 'Erro ao salvar o horário.' });
        } finally {
            setSalvando(false);
        }
    };

    const remover = async () => {
        setConfirmar(null);
        setSalvando(true);
        try {
            const r = await removeClassSchedule(id_class);
            setExiste(false);
            setCelulas({});
            setHorarios(Array.from({ length: LINHAS_PADRAO }, linhaVazia));
            setSabado(false);
            setMsg({ tom: 'verde', texto: r?.data?.msg || 'Horário removido.' });
        } catch (e) {
            setMsg({ tom: 'vermelho', texto: e?.response?.data?.msg || 'Erro ao remover o horário.' });
        } finally {
            setSalvando(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    const editavel = podeEditar && cargoGestor && !somenteLeitura;
    const nomeTurma = turma ? `${turma.serie || ''} ${turma.name || ''}`.trim() : '';

    const th = { padding: '8px 6px', fontSize: 13, fontWeight: 800, textAlign: 'center', borderBottom: '2px solid var(--line, #e3e8eb)' };
    const td = { padding: 4, borderBottom: '1px solid var(--line, #eef1f3)', textAlign: 'center' };

    return (
        <div style={{ width: '100%', padding: 16, boxSizing: 'border-box' }}>
            <style>{CSS_IMPRESSAO}</style>
            <div style={card}>
                <div id="grade-horaria-print">
                    <h2 style={{ margin: '0 0 4px' }}>🕒 Horário da turma {nomeTurma}</h2>
                    <p style={{ margin: '0 0 14px', color: 'var(--text-muted, #5b6b72)', fontSize: 14 }}>
                        {turma?.year && <>Ano {turma.year}</>}{turma?.shift && <> · Turno {turma.shift}</>}
                        {turma?.regente && <> · Regente: <strong>{turma.regente}</strong></>}
                        {turma?.adjunto && <> (adjunto {turma.adjunto} segue o horário do regente)</>}
                        {turma?.edFisica && <> · Ed. Física: <strong>{turma.edFisica}</strong></>}
                    </p>

                    {editavel && (
                        <div className="nao-imprimir" style={aviso('azul')}>
                            ℹ️ Com o horário salvo, cada professor <strong>só consegue lançar aulas e frequências nos dias em que
                            tem aula nesta turma</strong>. Ex.: se não houver Ed. Física na terça, o professor de Ed. Física não
                            lança nada nesta turma na terça. Preencha o horário <strong>completo</strong>.
                        </div>
                    )}
                    {!existe && (
                        <div className="nao-imprimir" style={aviso('laranja')}>
                            ⚠️ Esta turma ainda não tem horário{editavel ? '' : '. Peça à direção ou à secretaria para configurar'}.
                            Enquanto não houver horário, aulas e frequências funcionam normalmente em qualquer dia letivo.
                        </div>
                    )}

                    {editavel && (
                        <div className="nao-imprimir" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, cursor: 'pointer' }}>
                                <input type="checkbox" checked={sabado} onChange={(e) => setSabado(e.target.checked)} style={{ width: 18, height: 18 }} />
                                Tem aula aos sábados
                            </label>
                            <button type="button" onClick={preencherRegente} style={{ ...btn(false), padding: '8px 14px' }}>
                                Preencher vazios com Regente
                            </button>
                        </div>
                    )}

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...th, textAlign: 'left', width: 150 }}>Horário</th>
                                    {dias.map(d => <th key={d.id} style={th}>{d.nome}</th>)}
                                    {editavel && <th className="nao-imprimir" style={{ ...th, width: 34 }} />}
                                </tr>
                            </thead>
                            <tbody>
                                {horarios.map((h, i) => (
                                    <tr key={i}>
                                        <td style={{ ...td, textAlign: 'left', whiteSpace: 'nowrap' }}>
                                            <span style={{ fontWeight: 800, marginRight: 6 }}>{i + 1}º</span>
                                            {editavel ? (
                                                <>
                                                    <input type="time" value={h.inicio} onChange={(e) => setHora(i, 'inicio', e.target.value)} style={{ width: 82 }} aria-label={`Início do ${i + 1}º horário`} />
                                                    {' – '}
                                                    <input type="time" value={h.fim} onChange={(e) => setHora(i, 'fim', e.target.value)} style={{ width: 82 }} aria-label={`Fim do ${i + 1}º horário`} />
                                                </>
                                            ) : (
                                                <span>{h.inicio || h.fim ? `${h.inicio || '--:--'} – ${h.fim || '--:--'}` : ''}</span>
                                            )}
                                        </td>
                                        {dias.map(d => {
                                            const tipo = celulas[`${d.id}-${i}`] || '';
                                            const t = TIPOS[tipo];
                                            const estilo = {
                                                width: '100%', minWidth: 96, padding: '8px 6px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                                                border: `1px solid ${t ? t.borda : 'var(--line, #d0d7db)'}`,
                                                background: t ? t.bg : 'transparent', color: t ? t.cor : 'var(--text-muted, #8a979d)',
                                            };
                                            return (
                                                <td key={d.id} style={td}>
                                                    {editavel ? (
                                                        <select value={tipo} onChange={(e) => setCelula(d.id, i, e.target.value)} style={estilo} aria-label={`${d.nome}, ${i + 1}º horário`}>
                                                            <option value="">—</option>
                                                            <option value="REGENTE">Regente</option>
                                                            <option value="ED_FISICA">Ed. Física</option>
                                                        </select>
                                                    ) : (
                                                        <div style={{ ...estilo, boxSizing: 'border-box' }}>{t ? t.rotulo : '—'}</div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {editavel && (
                                            <td className="nao-imprimir" style={td}>
                                                <button type="button" onClick={() => removerLinha(i)} disabled={horarios.length <= 1} title="Remover este horário"
                                                    style={{ border: 'none', background: 'transparent', color: '#dc2626', fontSize: 18, cursor: 'pointer' }}>×</button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {editavel && (
                        <button type="button" className="nao-imprimir" onClick={addLinha} disabled={horarios.length >= 15}
                            style={{ ...btn(false), padding: '8px 14px', marginTop: 10 }}>
                            ＋ Adicionar horário
                        </button>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '16px 0 8px', fontSize: 14 }}>
                        {Object.entries(TIPOS).map(([k, t]) => (
                            <div key={k} style={{ background: t.bg, color: t.cor, border: `1px solid ${t.borda}`, borderRadius: 10, padding: '8px 12px' }}>
                                <strong>{t.rotulo}:</strong> {resumo[k].aulas} aula{resumo[k].aulas === 1 ? '' : 's'}/semana
                                {resumo[k].dias && <> · {resumo[k].dias}</>}
                            </div>
                        ))}
                    </div>
                </div>

                {editavel && faltaRegente && (
                    <div style={aviso('laranja')}>⚠️ Nenhuma aula de <strong>Regente</strong> marcada: o regente (e o adjunto) não conseguirão lançar aulas nem frequências nesta turma.</div>
                )}
                {editavel && faltaEdFisica && (
                    <div style={aviso('laranja')}>⚠️ Nenhuma aula de <strong>Ed. Física</strong> marcada: o professor de Ed. Física não conseguirá lançar aulas nem frequências nesta turma.</div>
                )}

                {msg && <div style={aviso(msg.tom)}>{msg.texto}</div>}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
                    <button type="button" onClick={() => navigate(-1)} style={btn(false)}>Voltar</button>
                    {existe && <button type="button" onClick={() => window.print()} style={btn(false)}>🖨️ Imprimir</button>}
                    {editavel && existe && (
                        <button type="button" onClick={() => setConfirmar('remover')} disabled={salvando}
                            style={{ ...btn(false), color: '#dc2626', borderColor: '#dc2626' }}>Remover horário</button>
                    )}
                    {editavel && (
                        <button type="button" onClick={pedirSalvar} disabled={salvando} style={btn(true)}>
                            {salvando ? 'Salvando…' : 'Salvar horário'}
                        </button>
                    )}
                </div>
            </div>

            {confirmar === 'salvar' && (
                <div style={overlay} onClick={() => setConfirmar(null)}>
                    <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <h3 style={{ margin: '0 0 10px' }}>⚠️ Antes de salvar</h3>
                        <p style={{ margin: '0 0 10px', fontSize: 15 }}>
                            Depois de salvar, os professores <strong>só vão conseguir lançar aulas e frequências nos dias em que têm
                            aula no horário desta turma</strong>:
                        </p>
                        <ul style={{ margin: '0 0 10px', paddingLeft: 20, fontSize: 15 }}>
                            <li><strong>Regente{turma?.adjunto ? ' e adjunto' : ''}:</strong> {resumo.REGENTE.dias || 'nenhum dia'}</li>
                            <li><strong>Ed. Física:</strong> {resumo.ED_FISICA.dias || 'nenhum dia'}</li>
                        </ul>
                        <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-muted, #5b6b72)' }}>
                            Não deixe o horário pela metade: um dia faltando impede o professor de lançar os dados nesse dia.
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setConfirmar(null)} style={btn(false)}>Cancelar</button>
                            <button type="button" onClick={salvar} style={btn(true)}>Continuar</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmar === 'remover' && (
                <div style={overlay} onClick={() => setConfirmar(null)}>
                    <div style={modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <h3 style={{ margin: '0 0 10px' }}>Remover o horário?</h3>
                        <p style={{ margin: '0 0 16px', fontSize: 15 }}>
                            Sem horário, a turma volta a aceitar aulas e frequências de todos os professores em qualquer dia letivo.
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => setConfirmar(null)} style={btn(false)}>Cancelar</button>
                            <button type="button" onClick={remover} style={{ ...btn(true), background: '#dc2626' }}>Remover</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassSchedule;
